import React from 'react';
import { useDispatch } from 'react-redux';
import { Box, Button, Typography, IconButton, Dialog, DialogTitle, DialogContent } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { IconX, IconArrowLeft, IconArrowRight } from '@tabler/icons-react';
import { IconBolt, IconInfoCircle, IconMapPin as IconMapPinFilled, IconPencil } from '@tabler/icons-react';

import { usePostBoxForm } from './hooks/usePostBoxForm';
import StepIndicator from './components/StepIndicator';
import Step1ActionTag from './components/steps/Step1ActionTag';
import Step2Issue from './components/steps/Step2Issue';
import Step3Location from './components/steps/Step3Location';
import Step4Content from './components/steps/Step4Content';
import '../PostBoxModal.css';

const stepIcons = {
  1: <IconBolt size={24} />,
  2: <IconInfoCircle size={24} />,
  3: <IconMapPinFilled size={24} />,
  4: <IconPencil size={24} />,
};

const stepLabels = {
  1: 'Action Tag',
  2: 'Issue',
  3: 'Location',
  4: 'Content',
};

/**
 * PostBoxModal - Multi-step post creation modal
 */
const PostBoxModal = ({ open, onClose, userData }) => {
  const dispatch = useDispatch();

  const {
    // State
    step,
    tag,
    issue,
    selectedScale,
    location,
    selectedLocation,
    title,
    content,
    images,
    locationSearchTerm,
    issueSearchTerm,
    isSubmitting,
    isSearchingLocation,
    locationError,
    selectedIssueStatus,
    contentFocused,
    setContentFocused,
    titleFocused,
    setTitleFocused,
    titleError,
    setTitleError,
    link,
    setLink,
    linkError,
    setLinkError,
    linkAnchorEl,
    setLinkAnchorEl,
    showLinkInput,
    setShowLinkInput,
    linkPreview,
    setLinkPreview,
    isLoadingPreview,
    locations,
    issuesFromRedux,
    isLoadingIssues,
    lastFetchedUrl,

    // Actions
    setStep,
    setTag,
    setIssue,
    setSelectedScale,
    setLocation,
    setSelectedLocation,
    setTitle,
    setContent,
    setImages,
    setLocationSearchTerm,
    setIssueSearchTerm,
    setSelectedIssueStatus,

    // Handlers
    handleClose,
    handleBack,
    handleLocationSearch,
    handleLocationSelect,
    handleIssueSearchChange,
    handleSubmit,
  } = usePostBoxForm({ open, onClose, userData });

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperComponent={motion.div}
      PaperProps={{
        initial: { opacity: 0, y: 20 },
        animate: { opacity: 1, y: 0 },
        exit: { opacity: 0, y: 20 },
        transition: { duration: 0.3 },
        sx: {
          bgcolor: 'background.paper',
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          borderRadius: '16px',
          padding: '0px',
          display: 'flex',
          flexDirection: 'column',
          height: '80vh',
          maxHeight: '85vh'
        }
      }}
      BackdropProps={{
        sx: { backgroundColor: 'rgba(0, 0, 0, 0.6)' }
      }}
    >
      <DialogTitle className="dialog-title">
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>Create a Post</Typography>
          <IconButton onClick={handleClose} size="small">
            <IconX size={20} />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent
        className="postbox-modal"
        sx={{ padding: '0px', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}
      >
        <Box className="step-indicators" display="flex" mb={0}>
          <StepIndicator stepNumber={1} current={step === 1} done={tag !== null} icon={stepIcons[1]} label={stepLabels[1]} />
          <StepIndicator stepNumber={2} current={step === 2} done={issue !== null} icon={stepIcons[2]} label={stepLabels[2]} />
          <StepIndicator stepNumber={3} current={step === 3} done={location !== "" || (selectedScale === 'global')} icon={stepIcons[3]} label={stepLabels[3]} />
          <StepIndicator stepNumber={4} current={step === 4} done={title !== "" || content !== ""} icon={stepIcons[4]} label={stepLabels[4]} />
        </Box>

        <AnimatePresence mode="wait">
          {step === 1 && (
            <Step1ActionTag
              tag={tag}
              setTag={setTag}
              setStep={setStep}
            />
          )}

          {step === 2 && (
            <Step2Issue
              issue={issue}
              setIssue={setIssue}
              setStep={setStep}
              issueSearchTerm={issueSearchTerm}
              handleIssueSearchChange={handleIssueSearchChange}
              issuesFromRedux={issuesFromRedux}
              isLoadingIssues={isLoadingIssues}
            />
          )}

          {step === 3 && (
            <Step3Location
              selectedScale={selectedScale}
              setSelectedScale={setSelectedScale}
              location={location}
              setLocation={setLocation}
              selectedLocation={selectedLocation}
              setSelectedLocation={setSelectedLocation}
              locationSearchTerm={locationSearchTerm}
              setLocationSearchTerm={setLocationSearchTerm}
              setStep={setStep}
              handleLocationSearch={handleLocationSearch}
              handleLocationSelect={handleLocationSelect}
              locations={locations}
              isSearchingLocation={isSearchingLocation}
              locationError={locationError}
              dispatch={dispatch}
            />
          )}

          {step === 4 && (
            <Step4Content
              tag={tag}
              issue={issue}
              selectedScale={selectedScale}
              location={location}
              title={title}
              setTitle={setTitle}
              content={content}
              setContent={setContent}
              images={images}
              setImages={setImages}
              selectedIssueStatus={selectedIssueStatus}
              setSelectedIssueStatus={setSelectedIssueStatus}
              titleFocused={titleFocused}
              setTitleFocused={setTitleFocused}
              titleError={titleError}
              setTitleError={setTitleError}
              contentFocused={contentFocused}
              setContentFocused={setContentFocused}
              link={link}
              setLink={setLink}
              linkError={linkError}
              setLinkError={setLinkError}
              linkAnchorEl={linkAnchorEl}
              setLinkAnchorEl={setLinkAnchorEl}
              showLinkInput={showLinkInput}
              setShowLinkInput={setShowLinkInput}
              linkPreview={linkPreview}
              setLinkPreview={setLinkPreview}
              isLoadingPreview={isLoadingPreview}
              lastFetchedUrl={lastFetchedUrl}
            />
          )}
        </AnimatePresence>

        <Box
          display="flex"
          justifyContent="space-between"
          className="postbox-modal-footer"
        >
          {step > 1 ? (
            <Button
              variant="text"
              startIcon={<IconArrowLeft size={16} />}
              onClick={handleBack}
            >
              Back
            </Button>
          ) : (
            <Button
              variant="text"
              color="error"
              onClick={handleClose}
            >
              Cancel
            </Button>
          )}

          {step >= 4 && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              disabled={isSubmitting || !title.trim() || !content.trim() || !selectedIssueStatus}
              endIcon={isSubmitting ? null : <IconArrowRight size={16} />}
            >
              {isSubmitting ? (
                <>
                  Posting...
                </>
              ) : 'Post'}
            </Button>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default PostBoxModal;
